import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FieldGroup } from "@/shared/components/ui/FieldGroup";
import { FormPage } from "@/shared/components/ui/FormPage";
import { FormField } from "@/shared/components/ui/FormField";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useState, type FormEvent } from "react";
import type { Client } from "../types/clientTypes";
import { userAction } from "@/core/telemetry/userAction";

interface ClientEditProps {
  client: Partial<Client>;
  onSubmit: (data: Partial<Client>) => void;
  isSubmitting: boolean;
  title: string;
  submitButtonText: string;
  error?: Error | null;
}

export function ClientEdit({ client, onSubmit, isSubmitting, title, submitButtonText, error }: ClientEditProps) {
  const [formData, setFormData] = useState({
    firstName: client.firstName || "",
    lastName: client.lastName || "",
    email: client.email || "",
    phoneNumber: client.phoneNumber || "",
    notes: client.notes || "",
  });
  type Field = keyof typeof formData;
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<Field, string>>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFieldErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    const errors: Partial<Record<Field, string>> = {};
    const values = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      notes: formData.notes.trim(),
    };
    const limits = { firstName: 100, lastName: 100, email: 254, phoneNumber: 40, notes: 2000 };
    for (const field of Object.keys(limits) as Field[]) {
      if (values[field].length > limits[field]) errors[field] = `Use ${limits[field]} characters or fewer.`;
    }
    if (!values.firstName) errors.firstName = "Enter a first name.";
    const emailInput = event.currentTarget.elements.namedItem("email") as HTMLInputElement;
    if (values.email && emailInput.validity.typeMismatch) errors.email = "Enter a valid email address.";
    if (values.phoneNumber && (!/^\+?[\d\s().-]+$/.test(values.phoneNumber) || !/^\d{7,15}$/.test(values.phoneNumber.replace(/\D/g, "")))) {
      errors.phoneNumber = "Enter a phone number with 7 to 15 digits. Spaces, +, parentheses and hyphens are allowed.";
    }
    setFieldErrors(errors);
    const firstError = (Object.keys(limits) as Field[]).find((field) => errors[field]);
    if (firstError) {
      (event.currentTarget.elements.namedItem(firstError) as HTMLElement)?.focus();
      return;
    }
    onSubmit(values);
  };

  return (
    <FormPage
      header={<PageHeader title={title} />}
      onSubmit={handleSubmit}
      actions={
        <Button type="submit" {...userAction("client.form_submit")} formNoValidate size="touch" disabled={isSubmitting}>{isSubmitting ? "Saving..." : submitButtonText}</Button>
      }
    >
      {error && <ErrorState message={error.message} />}
      <Section title="Customer information">
        <FieldGroup disabled={isSubmitting}>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField id="firstName" label="First name" required error={fieldErrors.firstName}>
              <Input name="firstName" autoComplete="given-name" maxLength={100} value={formData.firstName} onChange={handleChange} />
            </FormField>
            <FormField id="lastName" label="Last name" optional error={fieldErrors.lastName}>
              <Input name="lastName" autoComplete="family-name" maxLength={100} value={formData.lastName} onChange={handleChange} />
            </FormField>
          </div>
          <FormField id="email" label="Email" optional error={fieldErrors.email}>
            <Input type="email" name="email" autoComplete="email" maxLength={254} value={formData.email} onChange={handleChange} />
          </FormField>
          <FormField id="phoneNumber" label="Phone" optional error={fieldErrors.phoneNumber}>
            <Input type="tel" name="phoneNumber" autoComplete="tel" maxLength={40} value={formData.phoneNumber} onChange={handleChange} />
          </FormField>
          <FormField id="notes" label="Notes" optional error={fieldErrors.notes}>
            <TextArea name="notes" maxLength={2000} value={formData.notes} onChange={handleChange} rows={4} />
          </FormField>
        </FieldGroup>
      </Section>
    </FormPage>
  );
}
